import ReactMarkdown from "react-markdown"
import tw, { styled } from "twin.macro"
import { StyledBodyText } from "../bodyText"
import PageTitle from "../pageTitle"
import Image from "next/image"
import Zoom from "react-medium-image-zoom"
import Link from "next/link"
import { Attachment, FieldSet } from "airtable"

interface FeaturedImageProps {
  image_preview: Array<Attachment>
  image_url: string
  caption?: string
}
const FeaturedImage = ({ image_url, image_preview, caption }: FeaturedImageProps) => {
  const thisImage = image_preview[0].thumbnails && image_preview[0].thumbnails.large
  const width = thisImage && thisImage.width
  const height = thisImage && thisImage.height

  const altText = caption
    ? `${caption.toString()} – The Richard Pousette-Dart Foundation`
    : `The Richard Pousette-Dart Foundation`

  return (
    <Zoom
      zoomImg={{
        alt: altText,
        src: image_url,
        srcSet: image_url,
      }}
    >
      <Image
        key={image_preview[0].id}
        src={image_url}
        width={width}
        height={height}
        alt={altText}
        priority={true}
        sizes="(max-width: 768px) 100vw, 33vw"
      />

      {caption && (
        <div tw="py-2 px-2 text-rpd-gray-8">
          <StyledBodyText>
            <ReactMarkdown className="caption">{caption}</ReactMarkdown>
          </StyledBodyText>
        </div>
      )}
    </Zoom>
  )
}

const StyledCard = styled("div")(() => [
  tw`bg-rpd-blue px-6 py-5 flex justify-between items-center space-x-8 min-h-[120px]`,
])

const Arrow = () => {
  return (
    <svg width="15" height="56" viewBox="0 0 20 60" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 2L17 28.4727L2 58" stroke="#E6E3E3" strokeWidth="4" strokeLinecap="round" />
    </svg>
  )
}

interface DownloadProps {
  file: string
  link: boolean
}

const Download = ({ file, link }: DownloadProps) => {
  if (link) {
    return (
      <StyledBodyText>
        <Link href={file} target="_new" passHref tw="block font-sans" title={`Download the finding aid`}>
          Download the finding aid »
        </Link>
      </StyledBodyText>
    )
  }
  return (
    <Link href={file} target="_new" passHref tw="block hover:(text-white)" title={`Download the finding aid`}>
      <StyledCard>
        <p tw="text-white text-2xl" css="letter-spacing: -0.04em;">
          Download the finding aid
        </p>
        <div tw="h-full">
          <Arrow />
        </div>
      </StyledCard>
    </Link>
  )
}

export interface PageLayoutProps {
  children?: React.ReactNode
  id: string
  title: string
  body: string
  image_url: string
  image_preview: Array<Attachment>
  deck: string
  featured_caption: string
  pdf: string
  parentData?: FieldSet
}

export default function PageLayout(props: PageLayoutProps) {
  const { children, parentData, title, body, image_url, image_preview, deck, featured_caption, pdf } = props

  const bodyText = body && body != "\n" ? <ReactMarkdown>{body.toString()}</ReactMarkdown> : undefined
  const captionText = featured_caption ? featured_caption.toString() : undefined
  const parentTitle = parentData && parentData.title ? parentData.title.toString() : ""
  const parentUrl = parentData && parentData.parent_slug ? parentData.parent_slug.toString() : ""
  const parentType = parentData && parentData.type ? parentData.type.toString() : ""

  return (
    <>
      <section tw="py-10 space-y-10">
        <div tw="max-w-screen-desktop-lg desktop-lg:mx-auto px-4 relative">
          <div tw="grid grid-cols-4 tablet-lg:(grid-cols-12) gap-4 desktop:(gap-6)">
            <div tw="col-span-4 tablet-lg:(col-span-12)">
              {title && (
                <PageTitle
                  title={title}
                  parentUrl={parentUrl ?? undefined}
                  parentTitle={parentTitle}
                  type={parentType}
                />
              )}
            </div>
            {deck && (
              <div tw="col-span-4 tablet-lg:(col-span-12)">
                <div tw="text-xl font-thin tablet:(text-2xl)">
                  <ReactMarkdown>{deck.toString()}</ReactMarkdown>
                </div>
              </div>
            )}
          </div>
        </div>

        {bodyText && (
          <div tw="max-w-screen-desktop-lg desktop-lg:mx-auto px-4 relative">
            <div tw="grid grid-cols-4 gap-y-4 tablet:(grid-cols-12) gap-4 tablet:(gap-6 gap-y-0)">
              <div tw="col-span-4 tablet:(col-span-7)">
                <div tw="space-y-4 text-lg">
                  <StyledBodyText>{bodyText}</StyledBodyText>
                  {pdf && <Download link={true} file={pdf.toString()} />}
                </div>
              </div>
              {image_url && (
                <>
                  <div tw="col-span-8 order-first tablet:(col-start-8 col-span-5 order-last)">
                    <div tw="space-y-4">
                      <FeaturedImage
                        image_url={image_url.toString()}
                        image_preview={image_preview}
                        caption={captionText && captionText.toString()}
                      />
                      {pdf && <Download link={false} file={pdf.toString()} />}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {children && <>{children}</>}
      </section>
    </>
  )
}
